import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import { useMyAbilitiesQuery } from './api';
import { useAuth } from './AuthProvider';

import {
  buildAbility,
  canAll,
  EMPTY_ABILITY,
  type AbilityCheck,
  type AppAbility,
  type EmraAction,
  type EmraSubject,
} from '@/lib/casl';

// UI ability mirror (ADR-0005 §7.4, ADR-0009). Mounted inside <AuthProvider>
// around the router. It never holds back rendering. Consumers read `status`
// and stay fail-closed (everything hidden) until the rules are `ready`.
// The server checks every request regardless.

export type AbilityStatus = 'loading' | 'ready' | 'error';

interface AbilityContextValue {
  ability: AppAbility;
  status: AbilityStatus;
  refetch: () => void;
}

// Outside a provider every check is denied, so an isolated component spec
// stays fail-closed instead of crashing.
const FAIL_CLOSED: AbilityContextValue = {
  ability: EMPTY_ABILITY,
  status: 'loading',
  refetch: () => {},
};

const AbilityContext = createContext<AbilityContextValue>(FAIL_CLOSED);

interface AbilityProviderProps {
  children: ReactNode;
}

export function AbilityProvider({ children }: AbilityProviderProps): ReactNode {
  const { claims } = useAuth();
  const { data, isError, isSuccess, refetch } = useMyAbilitiesQuery(claims);

  const status = toStatus(isError, isSuccess);

  const ability = useMemo<AppAbility>(() => {
    if (data === undefined) {
      return EMPTY_ABILITY;
    }
    return buildAbility(data.rules);
  }, [data]);

  const value = useMemo<AbilityContextValue>(
    () => ({
      ability: status === 'ready' ? ability : EMPTY_ABILITY,
      status,
      refetch: () => {
        void refetch();
      },
    }),
    [ability, status, refetch],
  );

  return <AbilityContext.Provider value={value}>{children}</AbilityContext.Provider>;
}

// Error wins over stale data: a failed fetch hides affordances (ADR-0009)
// rather than trusting rules the server could not confirm.
function toStatus(isError: boolean, isSuccess: boolean): AbilityStatus {
  if (isError) {
    return 'error';
  }
  if (isSuccess) {
    return 'ready';
  }
  return 'loading';
}

export function useAbility(): AbilityContextValue {
  return useContext(AbilityContext);
}

/** True only when the rules have loaded and allow `action` on `subject`. */
export function useCan(action: EmraAction, subject: EmraSubject): boolean {
  const { ability, status } = useAbility();
  if (status !== 'ready') {
    return false;
  }
  return ability.can(action, subject);
}

/** True only when the rules have loaded and every check passes. */
export function useCanAll(checks: readonly AbilityCheck[]): boolean {
  const { ability, status } = useAbility();
  if (status !== 'ready') {
    return false;
  }
  return canAll(ability, checks);
}
